import { useEffect, useState, type FormEvent } from "react";
import { addDoc, collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, type Timestamp } from "firebase/firestore";
import { GoogleAuthProvider, getAuth, inMemoryPersistence, setPersistence, signInWithPopup, signOut } from "firebase/auth";
import { getApp, getApps, initializeApp } from "firebase/app";
import { Navigate, NavLink } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { CheckCircle2, Mail, Send, Users } from "lucide-react";
import { useAuth } from "./auth";
import { db, functions } from "./firebase";
import "./newsletter-admin.css";

const senderEmail = "info.mysterequebec@gmail.com";
type TemplateId = "lancement" | "nouveau-jeu" | "offre";
type Draft = { id: string; template: TemplateId; title: string; subject: string; preheader: string; body: string; ctaText: string; ctaUrl: string; updatedAt?: Timestamp; status?: "draft" | "sending" | "completed"; audienceCount?: number; trackingVersion?: number; startedAt?: Timestamp; bounceCheckedAt?: Timestamp; stats?: {sent:number;failed:number;bounced?:number;opened?:number;clicked:number;unsubscribed:number} };
type Subscriber = { id: string; email: string; status: string };
const templates: Record<TemplateId, Omit<Draft, "id" | "updatedAt">> = {
  lancement: {
    template: "lancement", title: "Lancement de Mystère Québec",
    subject: "Mystère Québec ouvre bientôt ses portes",
    preheader: "Une nouvelle aventure se prépare près de chez vous.",
    body: "Bonjour,\n\nLes premiers mystères de Québec prennent forme. Préparez votre équipe : bientôt, la ville deviendra votre terrain de jeu.\n\nÀ très bientôt,\nL’équipe Mystère Québec",
    ctaText: "Découvrir les jeux", ctaUrl: "https://mysterequebec.ca/jeux"
  },
  "nouveau-jeu": {
    template: "nouveau-jeu", title: "Annonce d’un nouveau jeu",
    subject: "Un nouveau mystère vous attend",
    preheader: "Découvrez le prochain parcours de Mystère Québec.",
    body: "Bonjour,\n\nUn nouveau jeu arrive à Québec. Rassemblez votre équipe et préparez-vous à suivre les indices.\n\nÀ bientôt sur le terrain,\nL’équipe Mystère Québec",
    ctaText: "Voir le nouveau jeu", ctaUrl: "https://mysterequebec.ca/jeux"
  },
  offre: {
    template: "offre", title: "Offre spéciale",
    subject: "Une invitation spéciale pour votre prochaine aventure",
    preheader: "Une occasion de découvrir Mystère Québec en équipe.",
    body: "Bonjour,\n\nNous avons une invitation spéciale pour vous. Venez vivre une aventure unique dans les rues de Québec.\n\nÀ bientôt,\nL’équipe Mystère Québec",
    ctaText: "Explorer les jeux", ctaUrl: "https://mysterequebec.ca/jeux"
  }
};
function rate(count: number, sent: number) {
  return sent > 0 ? new Intl.NumberFormat("fr-CA", {style:"percent", maximumFractionDigits:1}).format(count / sent) : "—";
}
function campaignStatus(campaign: Draft) {
  return campaign.status === "completed" ? "Terminée" : campaign.status === "sending" ? "Envoi en cours" : "Brouillon";
}
function campaignDate(campaign: Draft) {
  return campaign.startedAt?.toDate().toLocaleString("fr-CA") || "—";
}
function cleanUrl(value: string) {
  try { const url = new URL(value); return url.protocol === "https:" ? url.href : ""; } catch { return ""; }
}
function errorText(error: unknown) {
  const code = (error as {code?: string})?.code || "";
  if (code.includes("popup-closed") || code.includes("cancelled-popup")) return "Connexion annulée.";
  if (code.includes("unauthorized-domain")) return "Le domaine du site doit être autorisé dans Firebase Authentication.";
  if (code.includes("operation-not-allowed")) return "Activez la connexion Google dans Firebase Authentication.";
  return error instanceof Error ? error.message : "L’opération a échoué. Réessayez.";
}
function senderAuth() {
  const app = getApps().find(item => item.name === "newsletter-sender") || initializeApp(getApp().options, "newsletter-sender");
  return getAuth(app);
}
export function NewsletterAdmin() {
  const { profile } = useAuth();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [draft, setDraft] = useState<Omit<Draft, "id" | "updatedAt">>({ ...templates.lancement });
  const [draftId, setDraftId] = useState("");
  const [status, setStatus] = useState("");
  const [senderToken, setSenderToken] = useState("");
  const [bounceToken, setBounceToken] = useState("");
  const [syncing, setSyncing] = useState("");
  const [bounceCursors, setBounceCursors] = useState<Record<string,string>>({});
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const selectedCampaign = drafts.find(item => item.id === draftId);
  const locked = Boolean(selectedCampaign && selectedCampaign.status !== "draft");

  useEffect(() => {
    if (!profile?.isAdmin) return;
    const stopDrafts = onSnapshot(query(collection(db, "newsletterCampaigns"), orderBy("updatedAt", "desc"), limit(100)),
      snapshot => setDrafts(snapshot.docs.map(item => ({ id: item.id, ...item.data() } as Draft))),
      () => setStatus("Impossible de charger les brouillons."));
    const stopSubscribers = onSnapshot(query(collection(db, "newsletterSubscribers"), limit(1000)),
      snapshot => setSubscribers(snapshot.docs.map(item => ({ id: item.id, ...item.data() } as Subscriber))),
      () => setStatus("Impossible de charger les inscriptions."));
    return () => { stopDrafts(); stopSubscribers(); };
  }, [profile?.isAdmin]);

  if (!profile?.isAdmin) return <Navigate to="/" />;

  function selectTemplate(id: TemplateId) { setDraft({ ...templates[id] }); setDraftId(""); setStatus(""); }
  function selectDraft(id: string) {
    const selected = drafts.find(item => item.id === id);
    if (!selected) return;
    setDraftId(id);
    setDraft({ template: selected.template, title: selected.title, subject: selected.subject, preheader: selected.preheader, body: selected.body, ctaText: selected.ctaText, ctaUrl: selected.ctaUrl });
    setStatus("");
  }
  function edit(field: keyof typeof draft, value: string) { setDraft(current => ({ ...current, [field]: value })); setStatus(""); }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (locked) { setStatus("Cette campagne a déjà été lancée. Choisissez un nouveau modèle."); return; }
    setBusy(true);
    try {
      const data = { ...draft, title: draft.title.trim(), subject: draft.subject.trim(), preheader: draft.preheader.trim(), body: draft.body.trim(), ctaText: draft.ctaText.trim(), ctaUrl: draft.ctaUrl.trim(), status: "draft", updatedAt: serverTimestamp() };
      if (data.ctaUrl && !cleanUrl(data.ctaUrl)) throw new Error("Le lien du bouton doit commencer par https://.");
      if (draftId) await updateDoc(doc(db, "newsletterCampaigns", draftId), data);
      else { const created = await addDoc(collection(db, "newsletterCampaigns"), { ...data, createdAt: serverTimestamp() }); setDraftId(created.id); }
      setStatus("Brouillon enregistré.");
    } catch (error) { setStatus(errorText(error)); }
    finally { setBusy(false); }
  }
  async function connect() {
    setBusy(true); setStatus("");
    try {
      const auth = senderAuth();
      await setPersistence(auth, inMemoryPersistence);
      const provider = new GoogleAuthProvider();
      provider.addScope("https://www.googleapis.com/auth/gmail.send");
      provider.setCustomParameters({ prompt: "consent select_account", login_hint: senderEmail });
      const result = await signInWithPopup(auth, provider);
      const accessToken = GoogleAuthProvider.credentialFromResult(result)?.accessToken || "";
      if (result.user.email?.toLowerCase() !== senderEmail || !accessToken) {
        await signOut(auth);
        throw new Error("Sélectionnez le compte " + senderEmail + " pour autoriser l’envoi.");
      }
      setSenderToken(accessToken);
      setStatus("Compte Google autorisé pour cette session. Aucun mot de passe n’a été enregistré.");
    } catch (error) { setSenderToken(""); setStatus(errorText(error)); }
    finally { setBusy(false); }
  }
  async function disconnect() {
    setSenderToken(""); setBounceToken(""); await signOut(senderAuth()); setStatus("Compte déconnecté de cette session.");
  }
  async function connectBounceTracking() {
    if (!window.confirm("Le suivi des rebonds demande une permission Google de lecture de la boîte " + senderEmail + ". Cette permission donne techniquement accès à tous ses courriels. La plateforme ne recherchera que les avis d’échec et les messages envoyés nécessaires pour les relier aux campagnes. Autoriser pour cette session?")) return;
    setBusy(true); setStatus("");
    try {
      const auth = senderAuth();
      await setPersistence(auth, inMemoryPersistence);
      const provider = new GoogleAuthProvider();
      provider.addScope("https://www.googleapis.com/auth/gmail.send");
      provider.addScope("https://www.googleapis.com/auth/gmail.readonly");
      provider.setCustomParameters({prompt:"consent select_account", login_hint:senderEmail});
      const result = await signInWithPopup(auth, provider);
      const token = GoogleAuthProvider.credentialFromResult(result)?.accessToken || "";
      if (result.user.email?.toLowerCase() !== senderEmail || !token) {
        await signOut(auth); setSenderToken(""); setBounceToken("");
        throw new Error("Sélectionnez le compte " + senderEmail + ".");
      }
      setBounceToken(token); setSenderToken(token);
      setStatus("Suivi des rebonds autorisé pour cette session. Cliquez sur Vérifier les rebonds dans le tableau.");
    } catch (error) { setBounceToken(""); setStatus(errorText(error)); }
    finally { setBusy(false); }
  }
  async function syncBounces(campaignId: string) {
    if (!bounceToken || syncing || sending) return;
    setSyncing(campaignId);
    let pageToken = bounceCursors[campaignId] || "", scanned = 0, added = 0, unrecognized = 0;
    const sync = httpsCallable<{campaignId:string;accessToken:string;pageToken:string},{added:number;scanned:number;unrecognized:number;nextPageToken:string;complete:boolean}>(functions, "syncNewsletterBounces", {timeout:150000});
    try {
      for (let page = 0; page < 20; page++) {
        const result = await sync({campaignId,accessToken:bounceToken,pageToken});
        scanned += result.data.scanned; added += result.data.added; unrecognized += result.data.unrecognized;
        pageToken = result.data.nextPageToken;
        setBounceCursors(current => ({...current,[campaignId]:pageToken}));
        setStatus("Vérification des rebonds : " + scanned + " avis examinés, " + added + " nouveau(x) rebond(s). Gardez cette page ouverte.");
        if (result.data.complete) {
          setStatus("Vérification terminée : " + added + " nouveau(x) rebond(s), " + scanned + " avis examinés." + (unrecognized ? " " + unrecognized + " avis sans échec définitif identifiable n’ont pas été comptés." : "") + " Aucun rebond détecté ne constitue pas une confirmation de livraison.");
          return;
        }
      }
      setStatus("Vérification partielle : cliquez sur Continuer la vérification pour examiner les avis restants.");
    } catch (error) {setStatus("Vérification interrompue. Les rebonds déjà identifiés sont conservés. " + errorText(error));}
    finally {setSyncing("");}
  }
  async function testConnection() {
    if (!senderToken) return;
    setBusy(true);
    try {
      const subject = "Test de connexion · Mystère Québec";
      const mime = [
        "To: " + senderEmail,
        "From: " + senderEmail,
        "Subject: =?UTF-8?B?" + btoa(Array.from(new TextEncoder().encode(subject), byte => String.fromCharCode(byte)).join("")) + "?=",
        "MIME-Version: 1.0",
        "Content-Type: text/plain; charset=UTF-8",
        "",
        "Le compte d’envoi de Mystère Québec est connecté. Ce message est un test envoyé à votre propre adresse."
      ].join("\r\n");
      const raw = btoa(Array.from(new TextEncoder().encode(mime), byte => String.fromCharCode(byte)).join("")).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      const response = await fetch("https://gmail.googleapis.com/gmail/v1/users/me/messages/send", {
        method: "POST", headers: { Authorization: "Bearer " + senderToken, "Content-Type": "application/json" },
        body: JSON.stringify({ raw })
      });
      if (!response.ok) throw new Error(response.status === 401 ? "Autorisation expirée. Reconnectez le compte." : "Test impossible. Vérifiez que l’API Gmail est activée pour le projet Google (" + response.status + ").");
      setStatus("Courriel de test envoyé à " + senderEmail + ".");
    } catch (error) { setStatus(errorText(error)); }
    finally { setBusy(false); }
  }

  async function sendCampaign() {
    if (!draftId || !senderToken || locked || sending) return;
    if (!window.confirm("Envoyer cette campagne aux abonnés actifs? Cette opération ne peut pas être annulée après le premier envoi.")) return;
    setSending(true); setStatus("Envoi en cours… Gardez cette page ouverte.");
    const send = httpsCallable<{campaignId:string;accessToken:string},{sent:number;failed:number;remaining:number;completed:boolean;blocked?:string}>(functions, "sendNewsletterBatch");
    let totalSent = 0, totalFailed = 0;
    try {
      for (let batch = 0; batch < 25; batch++) {
        const result = await send({campaignId:draftId, accessToken:senderToken});
        totalSent += result.data.sent; totalFailed += result.data.failed;
        setStatus(totalSent + " courriel(s) transmis à Gmail, " + totalFailed + " échec(s), " + result.data.remaining + " restant(s).");
        if (result.data.blocked) {setStatus("Envoi interrompu : " + result.data.blocked + ". " + totalSent + " transmission(s) effectuée(s).");break;}
        if (result.data.completed || result.data.remaining === 0) break;
        if (result.data.failed > 0 && result.data.sent === 0) {setStatus("Envoi interrompu : " + totalSent + " transmis, " + totalFailed + " échec(s). Vérifiez l’autorisation Google et la configuration de l’API Gmail.");break;}
      }
    } catch (error) { setStatus("Envoi interrompu après " + totalSent + " transmission(s). " + errorText(error)); }
    finally { setSending(false); }
  }

  const subscribed = subscribers.filter(item => item.status === "subscribed").length;
  return <section className="page section newsletter-admin">
    <div className="section-heading"><span>Administration</span><h1>Infolettre</h1><p>Préparez vos campagnes et connectez l’adresse d’envoi de Mystère Québec.</p></div>
    <NavLink to="/admin" className="ghost newsletter-back">← Retour au centre de contrôle</NavLink>
    <div className="newsletter-admin-grid">
      <section className="panel newsletter-connection">
        <h2><Mail size={21}/> Adresse d’envoi</h2>
        <p><strong>{senderEmail}</strong></p>
        <p>{senderToken ? "Connexion Google active pour cette session." : "Autorisez l’envoi depuis ce compte avec Google. L’autorisation expire lorsque vous quittez cette page."}</p>
        <div className="form-actions">
          {senderToken ? <><button className="ghost" type="button" disabled={busy || Boolean(syncing) || sending} onClick={disconnect}>Déconnecter</button><button className="button" type="button" disabled={busy} onClick={testConnection}><Send size={16}/> Envoyer un test à cette adresse</button></> :
            <button className="button" type="button" disabled={busy} onClick={connect}>Connecter avec Google</button>}
        </div>
        <div className="form-actions"><button className="ghost" type="button" disabled={busy || Boolean(syncing) || sending || Boolean(bounceToken)} onClick={connectBounceTracking}>{bounceToken ? "Suivi des rebonds autorisé" : "Activer le suivi des rebonds"}</button></div>
        <small>Le suivi des rebonds nécessite une autorisation de lecture Gmail supplémentaire. La plateforme ne conserve pas le contenu des courriels et ne les marque pas comme lus. La vérification se fait à la demande, pendant cette session.</small>
        <small>La connexion dure le temps de cette page. Le test ne contacte aucun abonné. Gmail impose des limites quotidiennes d’envoi.</small>
      </section>
      <section className="panel newsletter-figures">
        <h2><Users size={21}/> Audience</h2>
        <strong>{subscribed}</strong><span>inscription{subscribed > 1 ? "s" : ""} active{subscribed > 1 ? "s" : ""}</span>
        <p>Les résultats de chaque campagne se mettent à jour automatiquement ci-dessous.</p>
      </section>
    </div>
    <div className="newsletter-admin-grid">
      <section className="panel newsletter-editor">
        <h2>Modèles et brouillons</h2>
        <label>Partir d’un modèle
          <select value={draft.template} onChange={event => selectTemplate(event.target.value as TemplateId)}>
            <option value="lancement">Lancement</option><option value="nouveau-jeu">Nouveau jeu</option><option value="offre">Offre spéciale</option>
          </select>
        </label>
        {drafts.length > 0 && <label>Reprendre un brouillon
          <select value={draftId} onChange={event => event.target.value ? selectDraft(event.target.value) : setDraftId("")}>
            <option value="">Choisir un brouillon</option>{drafts.map(item => <option key={item.id} value={item.id}>{item.title || item.subject}</option>)}
          </select>
        </label>}
        {locked && <p>Campagne lancée : créez un nouveau brouillon pour modifier le texte.</p>}
        <fieldset disabled={locked || sending} className="newsletter-fields"><form onSubmit={save}>
          <label>Nom interne<input required maxLength={120} value={draft.title} onChange={event => edit("title", event.target.value)}/></label>
          <label>Objet du courriel<input required maxLength={180} value={draft.subject} onChange={event => edit("subject", event.target.value)}/></label>
          <label>Aperçu dans la boîte de réception<input maxLength={220} value={draft.preheader} onChange={event => edit("preheader", event.target.value)}/></label>
          <label>Texte<textarea rows={9} required maxLength={10000} value={draft.body} onChange={event => edit("body", event.target.value)}/></label>
          <label>Texte du bouton<input maxLength={80} value={draft.ctaText} onChange={event => edit("ctaText", event.target.value)}/></label>
          <label>Lien du bouton<input type="url" maxLength={500} value={draft.ctaUrl} onChange={event => edit("ctaUrl", event.target.value)}/></label>
          <button className="button" disabled={busy || !draft.title.trim() || !draft.subject.trim() || !draft.body.trim()}><CheckCircle2 size={17}/> Enregistrer le brouillon</button>
        </form></fieldset>
      </section>
      <section className="panel newsletter-preview">
        <h2>Aperçu</h2>
        <div className="newsletter-preview-mail">
          <div className="newsletter-preview-meta"><strong>De :</strong> Mystère Québec &lt;{senderEmail}&gt;<br/><strong>Objet :</strong> {draft.subject || "Objet du courriel"}<br/><span>{draft.preheader}</span></div>
          <div className="newsletter-preview-content"><span className="newsletter-preview-brand">MQ · MYSTÈRE QUÉBEC</span><h3>{draft.subject || "Votre titre"}</h3><p>{draft.body || "Le contenu de votre campagne apparaîtra ici."}</p>{draft.ctaText && cleanUrl(draft.ctaUrl) && <a href={cleanUrl(draft.ctaUrl)} target="_blank" rel="noreferrer">{draft.ctaText}</a>}</div>
        </div>
        <p className="newsletter-preview-note">Vérifiez le texte et le lien avant l’envoi. Chaque abonné recevra un lien de désinscription individuel.</p>
        {selectedCampaign && <div className="newsletter-campaign-stats">
          <strong>Statut : {campaignStatus(selectedCampaign)}</strong>
          <span>Audience : {selectedCampaign.audienceCount ?? "à déterminer"}</span>
          <span>Acceptés par Gmail : {selectedCampaign.stats?.sent || 0}</span>
          <span>Échecs d’envoi : {selectedCampaign.stats?.failed || 0}</span>
          <span>Rebonds signalés : {selectedCampaign.bounceCheckedAt || selectedCampaign.stats?.bounced ? (selectedCampaign.stats?.bounced || 0) : "non vérifiés"}</span>
          <span>Ouvertures estimées : {selectedCampaign.trackingVersion === 2 ? (selectedCampaign.stats?.opened || 0) : "non mesurées"}</span>
          <span>Destinataires ayant cliqué : {selectedCampaign.stats?.clicked || 0}</span>
          <span>Désinscriptions : {selectedCampaign.stats?.unsubscribed || 0}</span>
        </div>}
        {draftId && !locked && <button className="button newsletter-send" type="button" disabled={!senderToken || sending || busy || subscribed === 0} onClick={sendCampaign}><Send size={17}/>{sending ? "Envoi en cours…" : "Envoyer aux abonnés actifs"}</button>}
        {!senderToken && <p className="newsletter-preview-note">Connectez le compte Gmail avant l’envoi.</p>}
      </section>
    </div>
    <section className="panel newsletter-history">
      <h2>Statistiques des campagnes</h2>
      <p>Chaque destinataire est compté une seule fois par campagne pour les ouvertures et les clics. Les taux utilisent le nombre de courriels acceptés par Gmail.</p>
      <div className="newsletter-table-wrap"><table>
        <caption>Les 100 campagnes les plus récentes</caption>
        <thead><tr><th>Campagne / début</th><th>Statut</th><th>Audience</th><th>Acceptés par Gmail</th><th>Échecs</th><th>Rebonds signalés</th><th>Ouvertures estimées</th><th>Clics uniques</th><th>Désinscriptions</th><th>Vérification des rebonds</th></tr></thead>
        <tbody>{drafts.filter(item => item.status === "sending" || item.status === "completed").map(item => {
          const sent = item.stats?.sent || 0;
          return <tr key={item.id}>
            <th scope="row"><button type="button" className="ghost" onClick={() => selectDraft(item.id)}>{item.title || item.subject}</button><small>{campaignDate(item)}</small></th>
            <td>{campaignStatus(item)}</td><td>{item.audienceCount ?? "—"}</td><td>{sent}</td><td>{item.stats?.failed || 0}</td>
            <td>{item.bounceCheckedAt || item.stats?.bounced ? <>{item.stats?.bounced || 0}<small>{rate(item.stats?.bounced || 0,sent)}</small></> : "Non vérifiés"}</td>
            <td>{item.trackingVersion === 2 ? <>{item.stats?.opened || 0}<small>{rate(item.stats?.opened || 0, sent)}</small></> : "Non mesurées"}</td>
            <td>{item.stats?.clicked || 0}<small>{rate(item.stats?.clicked || 0, sent)}</small></td>
            <td>{item.stats?.unsubscribed || 0}<small>{rate(item.stats?.unsubscribed || 0, sent)}</small></td>
          <td><button className="ghost" type="button" disabled={!bounceToken || Boolean(syncing) || sending || busy} onClick={() => syncBounces(item.id)}>{syncing === item.id ? "Vérification…" : bounceCursors[item.id] ? "Continuer la vérification" : "Vérifier les rebonds"}</button><small>{item.bounceCheckedAt ? "Dernière vérification complète : " + item.bounceCheckedAt.toDate().toLocaleString("fr-CA") : "Aucune vérification complète"}</small></td>
          </tr>;
        })}</tbody>
      </table></div>
      {!drafts.some(item => item.status === "sending" || item.status === "completed") && <p>Aucune campagne lancée pour le moment.</p>}
      <p className="newsletter-preview-note">Les ouvertures sont estimées à partir du chargement d’une image : les protections de confidentialité et les images bloquées peuvent modifier ces chiffres. Les outils de sécurité peuvent aussi déclencher des clics. Les rebonds sont les avis d’échec au format standard que Gmail permet de relier à cette campagne. Les retards, avis non reconnus et avis supprimés définitivement ne sont pas comptés. Zéro rebond signifie aucun échec identifié à la dernière vérification; la livraison réelle et la lecture du message restent non confirmées.</p>
    </section>
    {status && <p className="newsletter-admin-status" role="status">{status}</p>}
  </section>;
}
